import { toCamelCase, toSnakeCase } from 'remeda';

export type CamelCaseKeys<T> = T extends readonly unknown[]
  ? { [K in keyof T]: CamelCaseKeys<T[K]> }
  : T extends object
    ? {
        [K in keyof T as SnakeToCamel<K & string>]: CamelCaseKeys<T[K]>;
      }
    : T;

export type SnakeCaseKeys<T> = T extends readonly unknown[]
  ? { [K in keyof T]: SnakeCaseKeys<T[K]> }
  : T extends object
    ? {
        [K in keyof T as CamelToSnake<K & string>]: SnakeCaseKeys<T[K]>;
      }
    : T;

type CamelToSnake<S extends string> = S extends `${infer Head}${infer Tail}` ? `${Lowercase<Head>}${CamelToSnakeHelper<Tail>}` : S;

type CamelToSnakeHelper<S extends string> = S extends `${infer First}${infer Rest}` ? (First extends Uppercase<First> ? `_${Lowercase<First>}${CamelToSnakeHelper<Rest>}` : `${First}${CamelToSnakeHelper<Rest>}`) : '';

type SnakeToCamel<S extends string> = S extends `${infer Head}_${infer Tail}` ? `${Head}${Capitalize<SnakeToCamel<Tail>>}` : S;

const isPrimitive = (value: unknown) => ['boolean', 'number', 'string'].includes(typeof value);

const localize = <T>(object: T, toStyle: (string_: string, isUpper?: boolean) => string): CamelCaseKeys<T> | SnakeCaseKeys<T> | T => {
  // プリミティブ型なら変換しない
  if (object === null || isPrimitive(object)) {
    return object;
  }

  // 配列の中身がオブジェクトの場合のみキーを変換する
  if (Array.isArray(object)) {
    return object.map((a) => localize(a, toStyle)) as CamelCaseKeys<T> | SnakeCaseKeys<T>;
  }

  // オブジェクトのキーを変換する
  if (typeof object === 'object') {
    const destination: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(object)) {
      destination[toStyle(key)] = localize(value, toStyle);
    }
    return destination as CamelCaseKeys<T> | SnakeCaseKeys<T>;
  }

  return object;
};

export const toCamelCaseKeys = <T>(object: T) => localize(object, toCamelCase) as CamelCaseKeys<T>;
export const toSnakeCaseKeys = <T>(object: T) => localize(object, toSnakeCase) as SnakeCaseKeys<T>;
